/**
 * La MEDIA: un solo numero que resume al jugador, y de donde sale.
 *
 * Es la pieza que convierte una tabla de contadores en algo por lo que se
 * vuelve. Un jugador no recuerda que lleva 412 pases completados; recuerda que
 * es un 78 y que la semana pasada era un 76.
 *
 * Tres reglas que se han seguido a rajatabla:
 *
 *  1. **Nada de esto entra en la simulacion.** La media no toca al jugador
 *     dentro del campo: no corre mas ni chuta mejor. Es un espejo, no una
 *     ventaja — si diera ventaja, el que lleva 300 partidos seria intocable y
 *     el que llega hoy no volveria. Por eso puede usar `Math.pow` y compania
 *     sin miedo al lockstep.
 *  2. **Todo son funciones puras sobre `CareerTotals`.** Se prueban sin
 *     navegador, y la pantalla solo pinta.
 *  3. **Un partido no define a nadie.** Los ratios se mezclan con un promedio
 *     de partida (media bayesiana): con 1 partido pesas casi nada de ti mismo,
 *     con 20 ya casi todo. Sin esto, marcar un gol en tu primer partido te
 *     daria un 99 de remate y no habria a donde subir.
 */
import { t } from "../i18n/index.js";
import type { CareerTotals, Profile } from "./profile.js";

export type AttributeId = "finish" | "vision" | "passing" | "steal" | "control" | "impact";

export interface AttributeDef {
  id: AttributeId;
  /** Etiqueta corta, para el hexagono. */
  label: string;
  /** Que mide, en una linea. */
  hint: string;
  /** Cuanto pesa en la media. La suma de los seis es 100. */
  weight: number;
}

/**
 * Los seis ejes.
 *
 * Se han elegido porque son los unicos que el partido MIDE de verdad: cada uno
 * sale de contadores que `matchStats` ya lleva. Un eje de "velocidad" o
 * "regate" seria inventado, y un numero inventado en una ficha se nota.
 */
export const ATTRIBUTES: readonly AttributeDef[] = [
  { id: "finish", label: t("REMATE"), hint: t("Goles y puntería"), weight: 22 },
  { id: "vision", label: t("VISIÓN"), hint: t("Asistencias"), weight: 18 },
  { id: "passing", label: t("PASE"), hint: t("Pases que llegan"), weight: 15 },
  { id: "steal", label: t("ROBO"), hint: t("Balones recuperados"), weight: 15 },
  { id: "control", label: t("CONTROL"), hint: t("Balones que no pierdes"), weight: 12 },
  { id: "impact", label: t("DECISIVO"), hint: t("Victorias y MVP"), weight: 18 },
];

/** Suelo de la escala. Nadie baja de aqui: la ficha no puede ser un castigo. */
export const RATING_FLOOR = 40;
/** Techo. Se acerca pero no se toca sin una carrera enorme. */
export const RATING_CEIL = 99;

/**
 * Curva de un ratio a nota.
 *
 * `f(0) = 40`, `f(mid) = 75`, y tiende a 99 sin llegar nunca. Es una hiperbola,
 * no una recta, por una razon concreta: pasar de 0,2 a 0,4 goles por partido
 * tiene que notarse mucho, y de 2,0 a 2,2 casi nada. Una recta daria lo
 * contrario — todo el margen arriba, donde ya no hay nada que demostrar.
 */
export function curve(value: number, mid: number): number {
  if (mid <= 0) return RATING_FLOOR;
  const x = Math.max(0, value);
  // El 0,6857 sale de despejar f(mid) = 75 en 40 + 59·x/(x+a).
  const a = 0.6857142857 * mid;
  return RATING_FLOOR + (RATING_CEIL - RATING_FLOOR) * (x / (x + a));
}

/**
 * Media bayesiana: mezcla lo tuyo con un promedio de partida.
 *
 * `prior` es el ratio de alguien del monton y `k` cuantos partidos de
 * "credibilidad" vale ese promedio. Con `n = 0` sale el promedio; con `n` muy
 * grande, lo tuyo. Es lo que impide que el primer partido decida la carrera.
 */
export function shrink(total: number, n: number, prior: number, k: number): number {
  return (total + prior * k) / Math.max(1, n + k);
}

/** Cuantos partidos vale el promedio de partida. */
const PRIOR_MATCHES = 4;

/**
 * De donde arranca alguien que no ha demostrado nada.
 *
 * Es la constante mas importante de todo el fichero, y la que se calibro: el
 * promedio de partida NO es el de un jugador medio, es el de un NOVATO. Con un
 * promedio de jugador medio el debutante salia de 66 y el que llevaba cien
 * partidos llegaba a 80 — catorce puntos de recorrido para toda una carrera, y
 * una ficha que nunca se siente ganada. Con un novato de partida se sale de 58
 * y hay treinta y tantos puntos que subir.
 *
 * El numero sale de despejar la curva: `prior = 0,213 · mid` cae en un 54.
 */
const NOVICE = 0.213;

/**
 * Curva para lo que se mide en porcentaje.
 *
 * Un porcentaje de pases no vive entre 0 y 100: vive entre 45 y 90. Medirlo
 * desde cero mete a todo el mundo en la mitad alta de la escala y el atributo
 * deja de distinguir a nadie. `low` es donde empieza a contar de verdad.
 */
function band(value: number, low: number, mid: number): number {
  return curve(Math.max(0, value - low), mid - low);
}

export interface AttributeScore extends AttributeDef {
  /** 40-99. */
  value: number;
}

export interface PlayerRating {
  overall: number;
  attributes: AttributeScore[];
  /** El eje mas alto: de aqui sale el arquetipo. */
  best: AttributeScore;
  /** El eje mas bajo: lo que hay que ir a mejorar. */
  worst: AttributeScore;
  /** Nombre del tipo de jugador que dicen sus numeros. */
  archetype: string;
  /** Frase corta que explica el arquetipo. */
  archetypeHint: string;
  /** Partidos que faltan para que la media sea del todo tuya. */
  provisional: number;
}

function attributeValue(id: AttributeId, tot: CareerTotals): number {
  const n = tot.matches;
  switch (id) {
    case "finish": {
      // Goles por partido, empujado un poco por el acierto: dos jugadores con
      // el mismo gol por partido no valen lo mismo si uno tira el triple.
      const mid = 1.1;
      const perMatch = shrink(tot.goals, n, NOVICE * mid, PRIOR_MATCHES);
      const accuracy = tot.shots > 0 ? tot.goals / tot.shots : 0;
      return curve(perMatch * (1 + Math.min(0.5, accuracy)), mid);
    }
    case "vision": {
      const mid = 0.7;
      return curve(shrink(tot.assists, n, NOVICE * mid, PRIOR_MATCHES), mid);
    }
    case "passing": {
      // Porcentaje, con el novato metido en los propios contadores: 40 pases de
      // partida al 58%. Asi cuatro pases perfectos no dan un 99.
      const ok = tot.passesOk + 0.58 * 40 * PRIOR_MATCHES;
      const all = tot.passes + 40 * PRIOR_MATCHES;
      return band(ok / Math.max(1, all), 0.45, 0.8);
    }
    case "steal": {
      const mid = 6;
      return curve(shrink(tot.recoveries, n, NOVICE * mid, PRIOR_MATCHES), mid);
    }
    case "control": {
      // Balones conservados por balon tocado. El novato toca 30 y conserva el
      // 76%; conservar el 90% es lo que vale un 75.
      const kept = tot.touches - tot.turnovers + 0.76 * 30 * PRIOR_MATCHES;
      const all = tot.touches + 30 * PRIOR_MATCHES;
      return band(Math.max(0, kept) / Math.max(1, all), 0.62, 0.9);
    }
    case "impact": {
      // Ganar cuenta, pero ser el mejor del partido cuenta el doble: es lo
      // unico que no se puede conseguir yendo de pasajero en un equipo bueno.
      const mid = 1.2;
      const raw = tot.wins + tot.mvps * 2 + tot.cleanSheets * 0.5;
      return curve(shrink(raw, n, NOVICE * mid, PRIOR_MATCHES), mid);
    }
  }
}

const ARCHETYPES: Record<AttributeId, { name: string; hint: string }> = {
  finish: { name: t("KILLER"), hint: t("Si la ve, la mete") },
  vision: { name: t("MAESTRO"), hint: t("El pase que nadie ve") },
  passing: { name: t("METRÓNOMO"), hint: t("El equipo pasa por ti") },
  steal: { name: t("MURO"), hint: t("Por ahí no se pasa") },
  control: { name: t("IMÁN"), hint: t("El balón no se te va") },
  impact: { name: t("LÍDER"), hint: t("Contigo se gana") },
};

/**
 * Calcula la ficha entera.
 *
 * La media no es la media aritmetica de los seis ejes: se premia destacar. Un
 * jugador con 90 de remate y 55 en todo lo demas tiene que valer mas que uno
 * con seis sesentas, porque en el campo el primero decide partidos y el segundo
 * no. Por eso una cuarta parte de la nota sale de los DOS mejores ejes.
 */
export function ratePlayer(tot: CareerTotals): PlayerRating {
  const attributes: AttributeScore[] = ATTRIBUTES.map((def) => ({
    ...def,
    value: Math.round(attributeValue(def.id, tot)),
  }));

  const totalWeight = ATTRIBUTES.reduce((sum, a) => sum + a.weight, 0);
  const weighted =
    attributes.reduce((sum, a) => sum + a.value * a.weight, 0) / Math.max(1, totalWeight);

  // `ATTRIBUTES` nunca esta vacio, pero el compilador no lo sabe y un `!` aqui
  // seria una promesa sin respaldo. Este es el respaldo.
  const fallback: AttributeScore = { ...ATTRIBUTES[0]!, value: RATING_FLOOR };
  const sorted = [...attributes].sort((a, b) => b.value - a.value);
  const best = sorted[0] ?? fallback;
  const second = sorted[1] ?? best;
  const worst = sorted[sorted.length - 1] ?? best;
  const topTwo = (best.value + second.value) / 2;
  const overall = Math.round(weighted * 0.75 + topTwo * 0.25);
  const arch = ARCHETYPES[best.id];

  return {
    overall: Math.min(RATING_CEIL, Math.max(RATING_FLOOR, overall)),
    attributes,
    best,
    worst,
    archetype: arch.name,
    archetypeHint: arch.hint,
    provisional: Math.max(0, PRIOR_MATCHES * 2 - tot.matches),
  };
}

// ------------------------------------------------------------------- nivel

/** XP del primer nivel. Los siguientes crecen en `LEVEL_STEP`. */
const LEVEL_BASE = 300;
const LEVEL_STEP = 90;

/** XP que cuesta pasar del nivel `level` al siguiente. */
export function xpForLevel(level: number): number {
  return LEVEL_BASE + LEVEL_STEP * Math.max(0, level - 1);
}

export interface LevelInfo {
  level: number;
  /** XP dentro del nivel actual. */
  into: number;
  /** XP que pide este nivel entero. */
  need: number;
  /** 0-1, para la barra. */
  progress: number;
  division: string;
}

/**
 * Divisiones. No dan nada; son un nombre.
 *
 * Y precisamente por eso funcionan: el jugador que llega a PROFESIONAL no ha
 * desbloqueado un arma, ha desbloqueado una palabra que puede decir. Es lo
 * mismo que hace que alguien siga subiendo de rango en un juego de tiros.
 */
const DIVISIONS: readonly { from: number; name: string }[] = [
  { from: 50, name: t("LEYENDA") },
  { from: 35, name: t("CLASE MUNDIAL") },
  { from: 20, name: t("ÉLITE") },
  { from: 10, name: t("PROFESIONAL") },
  { from: 5, name: t("SEMIPRO") },
  { from: 1, name: t("AMATEUR") },
];

export function divisionFor(level: number): string {
  return DIVISIONS.find((d) => level >= d.from)?.name ?? t("AMATEUR");
}

/** Desmenuza la XP total en nivel y progreso. */
export function levelFor(xp: number): LevelInfo {
  let level = 1;
  let left = Math.max(0, Math.floor(xp));
  let need = xpForLevel(level);
  // Cota de seguridad: un perfil manipulado con XP absurda no puede colgar la
  // pantalla en un bucle de millones de vueltas.
  while (left >= need && level < 999) {
    left -= need;
    level += 1;
    need = xpForLevel(level);
  }
  return {
    level,
    into: left,
    need,
    progress: need > 0 ? left / need : 0,
    division: divisionFor(level),
  };
}

/** Pesos de la XP de un partido. Se exporta para poder probarlos. */
export const XP_RULES = {
  played: 20,
  win: 60,
  draw: 30,
  goal: 40,
  assist: 25,
  recovery: 3,
  shot: 2,
  passOk: 1,
  mvp: 75,
  cleanSheet: 20,
  /** Online multiplica: es donde hay alguien enfrente. */
  onlineFactor: 1.25,
} as const;

export interface XpInput {
  online: boolean;
  result: "win" | "draw" | "loss";
  goals: number;
  assists: number;
  shots: number;
  passesOk: number;
  recoveries: number;
  isMvp: boolean;
  cleanSheet: boolean;
}

export function xpForMatch(m: XpInput): number {
  const r = XP_RULES;
  let xp = r.played;
  if (m.result === "win") xp += r.win;
  else if (m.result === "draw") xp += r.draw;
  xp += m.goals * r.goal;
  xp += m.assists * r.assist;
  xp += m.recoveries * r.recovery;
  xp += m.shots * r.shot;
  // Los pases buenos suman poco y con tope: son cientos por partido, y sin tope
  // el que toca el balon sin arriesgar nada subiria mas rapido que el que juega.
  xp += Math.min(40, m.passesOk) * r.passOk;
  if (m.isMvp) xp += r.mvp;
  if (m.cleanSheet) xp += r.cleanSheet;
  return Math.round(xp * (m.online ? r.onlineFactor : 1));
}

/**
 * Forma: las ultimas cinco. Es lo que se ve de un vistazo antes que la media.
 *
 * Devuelve -2..+2 (de "en caida" a "en racha"), sin partidos suficientes = 0.
 */
export function formOf(profile: Profile): number {
  const last = profile.recent.slice(0, 5);
  if (last.length < 3) return 0;
  const points = last.reduce(
    (sum, m) => sum + (m.result === "win" ? 1 : m.result === "draw" ? 0 : -1),
    0,
  );
  const avg = points / last.length;
  if (avg >= 0.6) return 2;
  if (avg >= 0.2) return 1;
  if (avg <= -0.6) return -2;
  if (avg <= -0.2) return -1;
  return 0;
}
